import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Search, Filter, X, ChevronDown, ChevronRight, Briefcase, User, IndianRupee, Store, ArrowLeft, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import Layout from '../components/Layout';
import TemplateCardPreview from '../components/TemplateCardPreview';
import { useUserCard } from '../hooks/useUserCard';
import { useCart } from '../contexts/CartContext';
import { useToast } from '../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Category icons mapping
const categoryIcons = {
  'Professionals': Briefcase,
  'Individuals': User,
  'Finance & Banking': IndianRupee,
  'Indian SME & Entrepreneurs': Store,
};

// Debounce hook
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
    
    return () => clearTimeout(handler);
  }, [value, delay]);
  
  return debouncedValue;
};

const Templates = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { userCard, loading: cardLoading } = useUserCard();
  const { addToCart } = useCart();
  const { toast } = useToast();

  // State
  const [templates, setTemplates] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState(searchParams.get('q') || '');
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || 'all');
  const [selectedSubcategory, setSelectedSubcategory] = useState(searchParams.get('subcategory') || 'all');
  const [showFilters, setShowFilters] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState({});

  // Debounce search input
  const searchQuery = useDebounce(searchInput, 300);

  // Fetch templates and categories
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch categories
        const catResponse = await fetch(`${API_URL}/api/card-designer/categories`);
        const catData = await catResponse.json();
        setCategories(catData);
        
        // Build query params for templates
        const params = new URLSearchParams();
        if (searchQuery && searchQuery.length >= 2) {
          params.set('search', searchQuery);
        }
        if (selectedCategory !== 'all') {
          params.set('category_id', selectedCategory);
        }
        if (selectedSubcategory !== 'all') {
          params.set('subcategory_id', selectedSubcategory);
        }
        
        // Fetch templates with filters
        const templateUrl = `${API_URL}/api/card-designer/templates${params.toString() ? '?' + params.toString() : ''}`;
        const templateResponse = await fetch(templateUrl);
        const templateData = await templateResponse.json();
        setTemplates(templateData);
        
      } catch (error) {
        console.error('Error fetching templates:', error);
        toast({
          title: "Error",
          description: "Failed to load templates",
          variant: "destructive"
        });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [toast, searchQuery, selectedCategory, selectedSubcategory]);

  // Update URL params when filters change
  useEffect(() => {
    const params = new URLSearchParams();
    if (searchQuery) params.set('q', searchQuery);
    if (selectedCategory !== 'all') params.set('category', selectedCategory);
    if (selectedSubcategory !== 'all') params.set('subcategory', selectedSubcategory);
    setSearchParams(params, { replace: true });
  }, [searchQuery, selectedCategory, selectedSubcategory, setSearchParams]);

  // Templates are already filtered server-side, just use them directly
  const filteredTemplates = templates;

  // Get subcategories for selected category
  const subcategories = useMemo(() => {
    if (selectedCategory === 'all') return [];
    const category = categories.find(c => c.id === selectedCategory);
    return category?.subcategories || [];
  }, [categories, selectedCategory]);

  // Handle category selection
  const handleCategorySelect = (categoryId) => {
    setSelectedCategory(categoryId);
    setSelectedSubcategory('all');
  };

  // Toggle category expansion in mobile filter
  const toggleCategoryExpansion = (categoryId) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };

  // Handle add to cart
  const handleAddToCart = (template) => {
    addToCart({
      id: template.id,
      name: template.name,
      price: template.price,
      type: 'template',
      image: template.front_image
    });
    toast({
      title: "Added to Cart",
      description: `${template.name} has been added to your cart`,
    });
  };

  // Clear all filters
  const clearFilters = () => {
    setSearchInput('');
    setSelectedCategory('all');
    setSelectedSubcategory('all');
  };

  const hasActiveFilters = searchInput || selectedCategory !== 'all' || selectedSubcategory !== 'all';

  return (
    <Layout userCard={userCard} loading={cardLoading}>
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className="bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
            <div className="flex items-center gap-3 mb-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate(-1)}
                className="text-gray-300 hover:text-white hover:bg-white/10"
              >
                <ArrowLeft className="w-4 h-4 mr-1" />
                Back
              </Button>
            </div>
            
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-2">
              Digital Card Templates
            </h1>
            <p className="text-gray-300 text-sm sm:text-base max-w-2xl">
              Browse our collection of professional NFC card templates for every profession and business
            </p>

            {/* Search Bar */}
            <div className="mt-6 max-w-2xl">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Search templates by name, profession, or category..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  className="pl-12 pr-12 py-6 bg-white text-gray-900 rounded-xl border-0 shadow-lg text-base"
                  data-testid="template-search-input"
                />
                {searchInput && (
                  <button
                    onClick={() => setSearchInput('')}
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-5 h-5" />
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {/* Filter Bar */}
          <div className="flex flex-wrap items-center gap-3 mb-6">
            {/* Category Pills - Desktop */}
            <div className="hidden md:flex items-center gap-2 flex-wrap">
              <button
                onClick={() => handleCategorySelect('all')}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  selectedCategory === 'all'
                    ? 'bg-amber-500 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
                }`}
              >
                All Templates
              </button>
              {categories.map(category => {
                const IconComponent = categoryIcons[category.name] || Briefcase;
                return (
                  <button
                    key={category.id}
                    onClick={() => handleCategorySelect(category.id)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-2 ${
                      selectedCategory === category.id
                        ? 'bg-amber-500 text-white'
                        : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
                    }`}
                  >
                    <IconComponent className="w-4 h-4" />
                    {category.name}
                    {category.template_count > 0 && (
                      <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                        selectedCategory === category.id ? 'bg-white/20' : 'bg-gray-100'
                      }`}>
                        {category.template_count}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Mobile Filter Button */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="md:hidden flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-full text-sm font-medium"
            >
              <Filter className="w-4 h-4" />
              Filters
              {hasActiveFilters && (
                <span className="w-2 h-2 bg-amber-500 rounded-full"></span>
              )}
            </button>

            {/* Subcategory Pills */}
            {subcategories.length > 0 && (
              <div className="hidden md:flex items-center gap-2 ml-4 pl-4 border-l border-gray-300">
                <button
                  onClick={() => setSelectedSubcategory('all')}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                    selectedSubcategory === 'all'
                      ? 'bg-gray-800 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  All
                </button>
                {subcategories.map(sub => (
                  <button
                    key={sub.id}
                    onClick={() => setSelectedSubcategory(sub.id)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                      selectedSubcategory === sub.id
                        ? 'bg-gray-800 text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {sub.name}
                  </button>
                ))}
              </div>
            )}

            {/* Clear Filters */}
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="ml-auto text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1"
              >
                <X className="w-4 h-4" />
                Clear filters
              </button>
            )}
          </div>

          {/* Mobile Filters Panel */}
          {showFilters && (
            <div className="md:hidden bg-white rounded-xl border border-gray-200 p-4 mb-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-gray-900">Filter Templates</h3>
                <button onClick={() => setShowFilters(false)}>
                  <X className="w-5 h-5 text-gray-400" />
                </button>
              </div>
              
              <div className="space-y-2">
                <button
                  onClick={() => {
                    handleCategorySelect('all');
                    setShowFilters(false);
                  }}
                  className={`w-full text-left px-4 py-3 rounded-lg ${
                    selectedCategory === 'all' ? 'bg-amber-100 text-amber-800' : 'hover:bg-gray-50'
                  }`}
                >
                  All Templates
                </button>
                
                {categories.map(category => {
                  const IconComponent = categoryIcons[category.name] || Briefcase;
                  const isExpanded = expandedCategories[category.id];
                  
                  return (
                    <div key={category.id}>
                      <button
                        onClick={() => {
                          handleCategorySelect(category.id);
                          toggleCategoryExpansion(category.id);
                        }}
                        className={`w-full text-left px-4 py-3 rounded-lg flex items-center justify-between ${
                          selectedCategory === category.id ? 'bg-amber-100 text-amber-800' : 'hover:bg-gray-50'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <IconComponent className="w-4 h-4" />
                          {category.name}
                        </div>
                        <ChevronDown className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                      </button>
                      
                      {isExpanded && category.subcategories?.length > 0 && (
                        <div className="ml-6 mt-1 space-y-1">
                          {category.subcategories.map(sub => (
                            <button
                              key={sub.id}
                              onClick={() => {
                                setSelectedSubcategory(sub.id);
                                setShowFilters(false);
                              }}
                              className={`w-full text-left px-4 py-2 rounded-lg text-sm ${
                                selectedSubcategory === sub.id ? 'bg-gray-200 text-gray-900' : 'text-gray-600 hover:bg-gray-50'
                              }`}
                            >
                              {sub.name}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Results Count */}
          <div className="mb-4">
            <p className="text-sm text-gray-600">
              Showing <span className="font-semibold text-gray-900">{filteredTemplates.length}</span> templates
              {searchQuery && (
                <span> for "<span className="font-medium">{searchQuery}</span>"</span>
              )}
            </p>
          </div>

          {/* Templates Grid */}
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 text-amber-500 animate-spin" />
              <span className="ml-3 text-gray-600">Loading templates...</span>
            </div>
          ) : filteredTemplates.length === 0 ? (
            <div className="text-center py-20">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Search className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No templates found</h3>
              <p className="text-gray-500 mb-4">
                {searchQuery 
                  ? `No templates match "${searchQuery}"`
                  : "No templates available in this category"
                }
              </p>
              <Button onClick={clearFilters} variant="outline">
                Clear filters
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filteredTemplates.map(template => (
                <div
                  key={template.id}
                  className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow group"
                >
                  {/* Template Preview */}
                  <Link to={`/shop/template/${template.id}`}>
                    <div className="aspect-[1.6/1] bg-gray-100 relative overflow-hidden">
                      {template.front_image ? (
                        <img
                          src={template.front_image}
                          alt={template.name}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-800 to-gray-900">
                          <span className="text-white text-sm">Preview</span>
                        </div>
                      )}
                    </div>
                  </Link>

                  {/* Template Info */}
                  <div className="p-4">
                    <Link to={`/shop/template/${template.id}`}>
                      <h3 className="font-semibold text-gray-900 mb-1 group-hover:text-amber-600 transition-colors line-clamp-1">
                        {template.name}
                      </h3>
                    </Link>
                    
                    <p className="text-xs text-gray-500 mb-3">
                      {template.subcategory_name || template.category_name}
                    </p>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1">
                        <span className="text-lg font-bold text-gray-900">₹{template.price}</span>
                      </div>
                      
                      <Button
                        size="sm"
                        onClick={() => handleAddToCart(template)}
                        className="bg-amber-500 hover:bg-amber-600 text-white"
                        data-testid={`add-to-cart-${template.id}`}
                      >
                        Add to Cart
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default Templates;
